import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'environments/environment';
import { AuthService } from './auth.service';

export type RealtimeStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'denied'
  | 'disconnected';

export interface SubscribeAck {
  ok: boolean;
  channel?: string;
  error?: string;
}

/**
 * Reusable Socket.IO client for the /realtime namespace.
 *
 * One connection per browser window; a window can subscribe to multiple
 * channels on that single connection. New features: connect(branchId),
 * subscribe(channel), on(event, handler), disconnect().
 *
 * Not providedIn root so each component/window gets its own instance and
 * lifecycle. Provide it at the component level.
 */
@Injectable()
export class RealtimeService {
  private socket: Socket | null = null;
  private readonly status$ = new BehaviorSubject<RealtimeStatus>('idle');

  get status(): Observable<RealtimeStatus> {
    return this.status$.asObservable();
  }

  get currentStatus(): RealtimeStatus {
    return this.status$.value;
  }

  isConnected(): boolean {
    return !!this.socket?.connected;
  }

  /** Open the connection for a branch. No-op if already connected. */
  connect(branchId: string): void {
    if (this.socket) {
      return;
    }
    const token = AuthService.getToken();
    this.status$.next('connecting');

    // serverUrl ends with '/', so append the namespace directly.
    const url = `${environment.serverUrl}realtime`;
    this.socket = io(url, {
      transports: ['websocket'],
      auth: { token },
      query: { branchId, token },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    this.socket.on('connect', () => this.status$.next('connected'));

    this.socket.io.on('reconnect_attempt', () =>
      this.status$.next('reconnecting'),
    );
    this.socket.io.on('reconnect', () => this.status$.next('connected'));

    this.socket.on('disconnect', () => {
      // 'denied' is a terminal server rejection; don't overwrite it.
      if (this.status$.value !== 'denied') {
        this.status$.next('disconnected');
      }
    });

    this.socket.on('connect_error', (err: unknown) => {
      // Terminal auth rejection from the connection middleware: stop retrying.
      const code = (err as { data?: { code?: string } })?.data?.code;
      if (code === 'unauthorized') {
        this.status$.next('denied');
        this.socket?.disconnect();
        return;
      }
      if (this.status$.value !== 'denied') {
        this.status$.next('reconnecting');
      }
    });

    // Server rejected the handshake (bad token / no branch access).
    this.socket.on('connection_error', () => {
      this.status$.next('denied');
    });
  }

  /**
   * Subscribe to a channel. Resolves with the server ack; if the branch lacks
   * permission the ack has ok=false and status flips to 'denied'.
   */
  subscribe(channel: string, branchId: string): Promise<SubscribeAck> {
    return new Promise((resolve) => {
      if (!this.socket) {
        resolve({ ok: false, error: 'Not connected' });
        return;
      }
      this.socket.emit(
        'subscribe',
        { channel, branchId },
        (ack: SubscribeAck) => {
          if (ack && ack.ok === false) {
            this.status$.next('denied');
          }
          resolve(ack ?? { ok: false, error: 'No ack' });
        },
      );
    });
  }

  unsubscribe(channel: string, branchId: string): void {
    this.socket?.emit('unsubscribe', { channel, branchId });
  }

  /** Register a handler for a channel event (e.g. 'attendance.punch'). */
  on<T = unknown>(event: string, handler: (payload: T) => void): void {
    this.socket?.on(event, handler as (...args: unknown[]) => void);
  }

  off(event: string): void {
    this.socket?.off(event);
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.status$.next('idle');
  }
}
